// iter484 — First-visit educational disclaimer for the Nutrition Lab module.
// Required by EU AI Act Art. 50 (transparency) + GDPR Art. 13 (informed consent).
// Stored in localStorage so it shows ONCE per user/browser; can be re-opened
// anytime from the footer link "Информация" (NutritionLabPage).

import React, { useEffect, useState } from "react";
import { AlertTriangle, Shield, ShieldCheck, Sparkles, X } from "lucide-react";

const STORAGE_KEY = "nutramilo:foodlab:disclaimer:v1";

const T = {
  bg: {
    title:        "Образователен инструмент — НЕ е медицински съвет",
    intro:        "Nutrition Lab е аналитичен и образователен инструмент. Той помага да изследвате как храните взаимодействат с вашия метаболитен профил, но НЕ замества консултация с лекар, диетолог или ендокринолог.",
    list_title:   "Какво НЕ прави Nutrition Lab:",
    bullets: [
      "Не поставя медицинска диагноза",
      "Не предписва дози храни, добавки или медикаменти",
      "Не интерпретира лабораторни резултати с клиничен извод",
      "Не замества последването на терапия от лекуващ лекар",
    ],
    privacy_title: "Какво изпращаме до AI модела (GPT-5.2) при генерация на меню:",
    privacy_bullets: [
      "Избраните от Вас условия (напр. „PCOS“, „Хашимото“)",
      "Псевдонимизиран композитен фенотип (напр. „mild_insulin_resistance“)",
      "Целеви DIL/NIS/COL3 стойности (числа)",
    ],
    privacy_not:  "НЕ изпращаме: конкретни кръвни стойности, точно HOMA-IR число, имена на медикаменти, лични идентификатори.",
    legal_basis:  "Правно основание: чл. 6(1)(b) GDPR (договор) + чл. 9(2)(a) — изричен consent за обработка на здравни данни. OpenAI Inc. (САЩ) е под-процесор по чл. 28 GDPR със Standard Contractual Clauses (SCC) + opt-out от тренировка на моделите.",
    consent_check:"Разбирам, че Nutrition Lab е образователен инструмент и не замества медицинска консултация.",
    btn_accept:   "Разбрах — продължи",
    btn_cancel:   "Назад към началната страница",
    full_policy:  "Пълна Политика за поверителност",
  },
  en: {
    title:        "Educational Tool — NOT Medical Advice",
    intro:        "Nutrition Lab is an analytical and educational tool. It helps you explore how foods interact with your metabolic profile, but it does NOT replace consultation with a physician, dietitian, or endocrinologist.",
    list_title:   "What Nutrition Lab does NOT do:",
    bullets: [
      "Does not make a medical diagnosis",
      "Does not prescribe doses of foods, supplements or medications",
      "Does not interpret lab results as a clinical conclusion",
      "Does not replace your treating physician's care",
    ],
    privacy_title: "What we send to the AI model (GPT-5.2) when generating a menu:",
    privacy_bullets: [
      "Your selected conditions (e.g. \"PCOS\", \"Hashimoto\")",
      "Pseudonymised composite phenotype (e.g. \"mild_insulin_resistance\")",
      "Target DIL/NIS/COL3 values (numbers)",
    ],
    privacy_not:  "We do NOT send: specific lab values, exact HOMA-IR number, medication names, or personal identifiers.",
    legal_basis:  "Legal basis: Art. 6(1)(b) GDPR (contract) + Art. 9(2)(a) — explicit consent for health data processing. OpenAI Inc. (US) is a sub-processor under Art. 28 GDPR with Standard Contractual Clauses (SCC) + opt-out from model training.",
    consent_check:"I understand that Nutrition Lab is an educational tool and does not replace medical consultation.",
    btn_accept:   "Got it — continue",
    btn_cancel:   "Back to home",
    full_policy:  "Full Privacy Policy",
  },
  es: {
    title:        "Herramienta educativa — NO es consejo médico",
    intro:        "Nutrition Lab es una herramienta analítica y educativa. Te ayuda a explorar cómo los alimentos interactúan con tu perfil metabólico, pero NO reemplaza la consulta con un médico, dietista o endocrinólogo.",
    list_title:   "Lo que Nutrition Lab NO hace:",
    bullets: [
      "No realiza diagnósticos médicos",
      "No prescribe dosis de alimentos, suplementos ni medicamentos",
      "No interpreta resultados de laboratorio como conclusión clínica",
      "No reemplaza la atención de tu médico tratante",
    ],
    privacy_title: "Lo que enviamos al modelo de AI (GPT-5.2) al generar un menú:",
    privacy_bullets: [
      "Tus condiciones seleccionadas (p. ej. \"PCOS\", \"Hashimoto\")",
      "Fenotipo compuesto seudonimizado (p. ej. \"mild_insulin_resistance\")",
      "Valores objetivo de DIL/NIS/COL3 (números)",
    ],
    privacy_not:  "NO enviamos: valores específicos de laboratorio, número exacto de HOMA-IR, nombres de medicamentos ni identificadores personales.",
    legal_basis:  "Base legal: Art. 6(1)(b) RGPD (contrato) + Art. 9(2)(a) — consentimiento explícito para el tratamiento de datos de salud. OpenAI Inc. (EE. UU.) es un subencargado del tratamiento conforme al Art. 28 RGPD con Cláusulas Contractuales Estándar (SCC) + exclusión del entrenamiento del modelo.",
    consent_check:"Entiendo que Nutrition Lab es una herramienta educativa y no reemplaza la consulta médica.",
    btn_accept:   "Entendido — continuar",
    btn_cancel:   "Volver al inicio",
    full_policy:  "Política de privacidad completa",
  },
};

export function hasFoodlabConsent() {
  try { return localStorage.getItem(STORAGE_KEY) === "accepted_v1"; }
  catch { return false; }
}

export function resetFoodlabConsent() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

export default function NutritionLabDisclaimerModal({
  open,
  onAccept,
  onCancel,
  language = "bg",
  isDark = false,
}) {
  const lang = language === "en" ? "en" : "bg";
  const t = T[lang];
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!open) setAgreed(false);
  }, [open]);

  if (!open) return null;

  const accept = () => {
    if (!agreed) return;
    try { localStorage.setItem(STORAGE_KEY, "accepted_v1"); } catch {}
    onAccept?.();
  };

  const panelBg = isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200";
  const txt = isDark ? "text-slate-100" : "text-slate-900";
  const sub = isDark ? "text-slate-300" : "text-slate-600";
  const muted = isDark ? "text-slate-400" : "text-slate-500";
  const blockBg = isDark ? "bg-slate-800/60" : "bg-slate-50";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="foodlab-disclaimer-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="foodlab-disclaimer-title"
    >
      <div className={`relative w-full max-w-2xl rounded-2xl border shadow-2xl ${panelBg} max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className="flex items-start gap-3 p-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="foodlab-disclaimer-title" className={`text-lg sm:text-xl font-bold ${txt}`}>
              {t.title}
            </h2>
            <p className={`text-xs sm:text-sm mt-1 ${sub}`}>EU AI Act · GDPR Art. 13 · Educational</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className={`p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 ${muted}`}
            aria-label="Close"
            data-testid="foodlab-disclaimer-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 pt-4 space-y-4">
          <p className={`text-sm leading-relaxed ${txt}`}>{t.intro}</p>

          <div className={`rounded-xl border p-4 ${blockBg} border-slate-200 dark:border-slate-700`}>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-rose-500" />
              <span className={`text-xs font-bold uppercase tracking-wide ${txt}`}>{t.list_title}</span>
            </div>
            <ul className={`space-y-1.5 text-sm ${sub}`}>
              {t.bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-rose-500 shrink-0">✕</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className={`rounded-xl border p-4 ${blockBg} border-slate-200 dark:border-slate-700`}>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <span className={`text-xs font-bold uppercase tracking-wide ${txt}`}>{t.privacy_title}</span>
            </div>
            <ul className={`space-y-1.5 text-sm ${sub} mb-3`}>
              {t.privacy_bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-emerald-500 shrink-0">✓</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className={`text-xs ${muted} flex gap-2`}>
              <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" />
              <span>{t.privacy_not}</span>
            </div>
          </div>

          <p className={`text-[11px] leading-relaxed ${muted}`}>{t.legal_basis}</p>

          <a
            href="/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs underline hover:text-violet-500 ${muted}`}
            data-testid="foodlab-disclaimer-privacy-link"
          >
            {t.full_policy} →
          </a>
        </div>

        {/* Footer / Consent */}
        <div className={`p-6 pt-0 border-t border-slate-200 dark:border-slate-700 mt-2 pt-4`}>
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-4 h-4 accent-violet-600"
              data-testid="foodlab-disclaimer-checkbox"
            />
            <span className={`text-sm ${txt}`}>{t.consent_check}</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              className={`px-4 py-2 rounded-lg text-sm font-medium border ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              data-testid="foodlab-disclaimer-cancel"
            >
              {t.btn_cancel}
            </button>
            <button
              type="button"
              onClick={accept}
              disabled={!agreed}
              className={`px-5 py-2 rounded-lg text-sm font-bold text-white transition ${agreed ? "bg-gradient-to-r from-violet-600 to-cyan-600 hover:opacity-90" : "bg-slate-400 cursor-not-allowed opacity-60"}`}
              data-testid="foodlab-disclaimer-accept"
            >
              {t.btn_accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
